import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  useTheme,
  alpha,
  Button,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CurrencyPoundIcon from '@mui/icons-material/CurrencyPound';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { getImagesPublic } from 'src/apiCalls';
import type { Image } from "src/interfaces";
import logger from 'src/logger';

const HomePage = () => {
  const theme = useTheme();
  const [premiumSpots, setPremiumSpots] = useState<Image[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchPremiumSpots = async () => {
      try {
        setLoading(true);
        const data = await getImagesPublic({ 
          locationOnly: true,
          chargeForLocation: true 
        });
        
        if (data.images && data.images.length > 0) {
          // Randomize the order of images
          const shuffledImages = [...data.images].sort(() => Math.random() - 0.5);
          setPremiumSpots(shuffledImages);
        }
      } catch (error) {
        logger.error('Error fetching premium spots:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPremiumSpots();
  }, []);

  useEffect(() => {
    if (premiumSpots.length > 0) {
      const timer = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % premiumSpots.length);
      }, 5000); // Change spot every 5 seconds
      return () => clearInterval(timer);
    }
  }, [premiumSpots.length]);

  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % premiumSpots.length);
  };

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + premiumSpots.length) % premiumSpots.length);
  };

  const currentSpot = premiumSpots[currentIndex];

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        pb: 2
      }}
    >
      <Container
        maxWidth="xl"
        disableGutters
        sx={{
          position: 'relative',
          zIndex: 2,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          width: '100%',
          px: { xs: 1, sm: 2 }
        }}
      >
        {/* Hero Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            mb: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.light}15 0%, ${theme.palette.secondary.light}15 100%)`,
            borderRadius: '2px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography
                  variant="h2"
                  component="h1"
                  sx={{
                    fontWeight: 'bold',
                    mb: 3,
                    fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem', lg: '4rem' },
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em'
                  }}
                >
                  Buy & Sell Fishing Spots
                </Typography>
                <Typography 
                  variant="h5" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 4,
                    fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.4rem' },
                    lineHeight: 1.4,
                    maxWidth: { xs: '100%', md: '90%' }
                  }}
                >
                  Find your next hotspot or earn money sharing your successful locations. Access premium fishing spots from successful anglers or list your own spots for others.
                </Typography>
              </Box>
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                flexDirection: { xs: 'column', sm: 'row' },
                width: '100%',
                justifyContent: { xs: 'center', md: 'flex-start' },
                alignItems: 'center'
              }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  fullWidth
                  startIcon={<LocationOnIcon />}
                  component={RouterLink}
                  to="/browse"
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
                    py: { xs: 2, sm: 1.5 },
                    px: 3,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 'bold',
                    minHeight: { xs: '56px', sm: '48px' },
                    '&:hover': {
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
                    }
                  }}
                >
                  Browse Premium Spots
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  fullWidth
                  startIcon={<CurrencyPoundIcon />}
                  component={RouterLink}
                  to="/images/addsingle"
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    borderWidth: 2,
                    py: { xs: 2, sm: 1.5 },
                    px: 3,
                    fontSize: { xs: '1rem', sm: '1.1rem' },
                    fontWeight: 'bold',
                    minHeight: { xs: '56px', sm: '48px' },
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-3px)',
                      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
                    }
                  }}
                >
                  Add Your Spot
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.15)} 0%, ${alpha(theme.palette.secondary.light, 0.15)} 100%)`,
                  borderRadius: '2px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}
              >
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    mb: { xs: 1.5, sm: 2 },
                    fontWeight: 'bold',
                    color: theme.palette.primary.dark,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}
                >
                  Discover Where They Caught It
                </Typography>
                {loading ? (
                  <Card
                    sx={{
                      borderRadius: '2px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                      overflow: 'hidden',
                      height: { xs: '250px', sm: '300px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography>Loading premium spots...</Typography>
                  </Card>
                ) : premiumSpots.length > 0 ? (
                  <Box sx={{ position: 'relative' }}>
                    <Card
                      sx={{
                        borderRadius: '2px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                        overflow: 'hidden',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'none'
                        }
                      }}
                      component={RouterLink}
                      to={`/image/${currentSpot.id}`}
                    >
                      <CardMedia
                        component="img"
                        height={300}
                        image={currentSpot.path}
                        alt="Premium Fishing Location"
                        sx={{
                          height: { xs: 200, sm: 300 },
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      />
                      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                          <LocationOnIcon color="primary" sx={{ mr: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                          <Box>
                            <Typography 
                              variant="h6" 
                              component="h2"
                              sx={{
                                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                fontWeight: 'bold',
                                color: theme.palette.primary.dark
                              }}
                            >
                              {currentSpot.species || 'Premium Fishing Location'}
                            </Typography>
                            {currentSpot.area && (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: theme.palette.text.secondary,
                                  fontSize: { xs: '0.8rem', sm: '0.9rem' }
                                }}
                              >
                                {currentSpot.area}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            mb: 2,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            lineHeight: 1.4
                          }}
                        >
                          {currentSpot.tags || 'This premium location has produced multiple trophy catches. Access detailed coordinates and fishing tips.'}
                        </Typography>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start',
                          mt: 'auto'
                        }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', pt: 0.5 }}>
                            <Chip
                              label="Premium Location"
                              color="primary"
                              sx={{
                                borderRadius: '2px',
                                fontWeight: 'bold',
                                height: { xs: 28, sm: 32 },
                                backgroundColor: theme.palette.success.main,
                                '& .MuiChip-label': {
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  px: { xs: 1, sm: 2 }
                                }
                              }}
                            />
                          </Box>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'flex-end'
                          }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: theme.palette.text.secondary,
                                mb: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <LocationOnIcon sx={{ fontSize: '0.9rem' }} />
                              Location Access
                            </Typography>
                            <Box sx={{ 
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              backgroundColor: currentSpot.price ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.success.main, 0.1),
                              px: 1,
                              py: 0.5,
                              borderRadius: '4px'
                            }}>
                              {currentSpot.price ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <LocationOnIcon sx={{ fontSize: '1rem', color: theme.palette.primary.main }} />
                                  <LockIcon sx={{ fontSize: '0.9rem', color: theme.palette.primary.main }} />
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <LocationOnIcon sx={{ fontSize: '1rem', color: theme.palette.success.main }} />
                                  <LockOpenIcon sx={{ fontSize: '0.9rem', color: theme.palette.success.main }} />
                                </Box>
                              )}
                              <Typography 
                                variant="h6" 
                                color={currentSpot.price ? "primary" : "success"}
                                sx={{
                                  fontSize: { xs: '1rem', sm: '1.1rem' },
                                  fontWeight: 'bold'
                                }}
                              >
                                {currentSpot.price ? `£${currentSpot.price}` : 'Free'}
                              </Typography>
                            </Box>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                color: theme.palette.text.secondary,
                                mt: 0.5,
                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                textAlign: 'right',
                                maxWidth: '120px'
                              }}
                            >
                              {currentSpot.price 
                                ? currentSpot.freeForRegisteredUsers 
                                  ? 'Free for registered users'
                                  : 'Price to reveal exact location'
                                : currentSpot.freeForRegisteredUsers
                                  ? 'Free for registered users'
                                  : currentSpot.freeForEveryone
                                    ? 'Free for everyone'
                                    : 'Location included'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                    <IconButton
                      onClick={handlePrevious}
                      sx={{
                        position: 'absolute',
                        left: { xs: 4, sm: 8 },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: alpha(theme.palette.common.black, 0.85),
                        color: theme.palette.common.white,
                        '&:hover': {
                          backgroundColor: theme.palette.common.black,
                        },
                        width: { xs: 36, sm: 40 },
                        height: { xs: 36, sm: 40 },
                        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                        border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`
                      }}
                    >
                      <NavigateBeforeIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
                    </IconButton>
                    <IconButton
                      onClick={handleNext}
                      sx={{
                        position: 'absolute',
                        right: { xs: 4, sm: 8 },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: alpha(theme.palette.common.black, 0.85),
                        color: theme.palette.common.white,
                        '&:hover': {
                          backgroundColor: theme.palette.common.black,
                        },
                        width: { xs: 36, sm: 40 },
                        height: { xs: 36, sm: 40 },
                        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                        border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`
                      }}
                    >
                      <NavigateNextIcon sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} />
                    </IconButton>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: { xs: 8, sm: 12 },
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: { xs: 0.5, sm: 1 },
                        backgroundColor: alpha(theme.palette.common.black, 0.5),
                        padding: '6px 12px',
                        borderRadius: '16px',
                        zIndex: 1
                      }}
                    >
                      {premiumSpots.map((_, index) => (
                        <Box
                          key={index}
                          sx={{
                            width: { xs: 6, sm: 8 },
                            height: { xs: 6, sm: 8 },
                            borderRadius: '50%',
                            backgroundColor: index === currentIndex
                              ? theme.palette.common.white
                              : alpha(theme.palette.common.white, 0.5),
                            cursor: 'pointer',
                            transition: 'background-color 0.3s',
                            '&:hover': {
                              backgroundColor: theme.palette.common.white
                            }
                          }}
                          onClick={() => setCurrentIndex(index)}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Card
                    sx={{
                      borderRadius: '2px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                      overflow: 'hidden',
                      height: { xs: '250px', sm: '300px' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Typography>No premium spots available at the moment</Typography>
                  </Card>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* How It Works Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 4 },
            mb: 2,
            background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.light}15 100%)`,
            borderRadius: '2px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 'bold',
              mb: 4,
              textAlign: 'center',
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            How It Works
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <LocationOnIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                  Find Great Spots
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Browse through verified fishing spots shared by successful anglers
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <CurrencyPoundIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                  Buy or Sell Locations
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Purchase access to premium spots or earn money by sharing your successful locations
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <EmojiEventsIcon sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 2 }} />
                <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                  Catch More Fish
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Use insider knowledge to improve your fishing success
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Competition Banner - Moved to bottom and made less prominent */}
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: '2px',
            p: { xs: 1.5, sm: 2 },
            mb: 2,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, sm: 0 } }}>
            <EmojiEventsIcon 
              sx={{ 
                color: theme.palette.primary.main,
                fontSize: { xs: '1.5rem', sm: '2rem' },
                mr: 1.5
              }} 
            />
            <Box>
              <Typography 
                variant="h6" 
                component="h2" 
                sx={{ 
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  fontWeight: 'bold',
                  color: theme.palette.primary.dark,
                  lineHeight: 1.2
                }}
              >
                Join Our Photo Competitions
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                Enter your best catch & vote for your favorites!
              </Typography>
            </Box>
          </Box>
          <Button 
            component={RouterLink} 
            to="/competitions"
            variant="outlined"
            color="primary"
            size="small"
            endIcon={<ArrowForwardIcon />}
            sx={{ 
              fontWeight: 'bold',
              textTransform: 'none',
              borderRadius: '2px',
              px: 2,
              py: 0.5
            }}
          >
            View Competitions
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default HomePage;
